"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import PhonePrefixPicker from "@/components/ui/phone-prefix-picker";
import { buildWhatsAppClickEvent, toServicioSlug } from "@/lib/lead-tracking";
import { createRefId, getStoredAttribution, pushDataLayer } from "@/lib/lead-tracking-client";

type Locale = "es" | "en";
type ServiceOption = {
  optionId: string;
  serviceId: string;
  canonicalServiceId: string;
  selectionLabel: string;
  category: string;
  aliases?: string[];
  searchTokens?: string[];
};

type ServiceOptionsApiResponse = {
  options?: ServiceOption[];
};

type WhatsAppLeadLinkProps = {
  source: string;
  locale?: Locale;
  className?: string;
  title?: string;
  children: ReactNode;
};

const COPY = {
  es: {
    title: "Contacto por WhatsApp",
    subtitle: "Cuéntanos el procedimiento y abrimos WhatsApp con el contexto preparado.",
    name: "Nombre",
    namePlaceholder: "Tu nombre completo",
    phone: "Teléfono / WhatsApp",
    phonePlaceholder: "600 000 000",
    procedure: "Procedimiento legal",
    defaultProcedure: "Consulta legal",
    procedurePlaceholder: "Buscar servicio...",
    servicesLoading: "Cargando servicios...",
    servicesError: "No se pudo cargar el catálogo. Puedes continuar con Consulta legal.",
    noServices: "Sin resultados",
    cancel: "Cancelar",
    submit: "Abrir WhatsApp",
    sending: "Abriendo...",
    nameError: "Introduce tu nombre.",
    phoneError: "Introduce un teléfono válido.",
    procedureError: "Indica el procedimiento o caso que necesitas consultar.",
    submitError: "No se pudo registrar el contacto. Inténtalo de nuevo.",
  },
  en: {
    title: "WhatsApp Contact",
    subtitle: "Tell us the legal procedure and we will open WhatsApp with the context ready.",
    name: "Name",
    namePlaceholder: "Your full name",
    phone: "Phone / WhatsApp",
    phonePlaceholder: "600 000 000",
    procedure: "Legal procedure",
    defaultProcedure: "Legal consultation",
    procedurePlaceholder: "Search service...",
    servicesLoading: "Loading services...",
    servicesError: "Service catalog could not be loaded. You can continue with Legal consultation.",
    noServices: "No results",
    cancel: "Cancel",
    submit: "Open WhatsApp",
    sending: "Opening...",
    nameError: "Enter your name.",
    phoneError: "Enter a valid phone number.",
    procedureError: "Enter the procedure or case you need help with.",
    submitError: "We could not register the contact. Please try again.",
  },
} satisfies Record<Locale, Record<string, string>>;

function isValidPhone(value: string): boolean {
  const digits = value.replace(/[^\d]/g, "");
  return digits.length >= 8 && digits.length <= 15;
}

function normalizeToken(value: string): string {
  return value.trim().toLowerCase();
}

export default function WhatsAppLeadLink({
  source,
  locale = "es",
  className,
  title,
  children,
}: WhatsAppLeadLinkProps) {
  const copy = COPY[locale];
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [phonePrefix, setPhonePrefix] = useState("+34");
  const [procedureOptionId, setProcedureOptionId] = useState("legal-consultation");
  const [procedureSearch, setProcedureSearch] = useState("");
  const [procedureOpen, setProcedureOpen] = useState(false);
  const [serviceOptions, setServiceOptions] = useState<ServiceOption[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesLoadError, setServicesLoadError] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const procedureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    const controller = new AbortController();

    async function loadServiceOptions() {
      setServicesLoading(true);
      setServicesLoadError(false);
      try {
        const response = await fetch(`/api/services/options?locale=${locale}`, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) throw new Error("services_failed");
        const payload = (await response.json()) as ServiceOptionsApiResponse;
        if (active) setServiceOptions(Array.isArray(payload.options) ? payload.options : []);
      } catch {
        if (!active || controller.signal.aborted) return;
        setServiceOptions([]);
        setServicesLoadError(true);
      } finally {
        if (active) setServicesLoading(false);
      }
    }

    loadServiceOptions();
    return () => {
      active = false;
      controller.abort();
    };
  }, [locale, open]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (procedureRef.current && !procedureRef.current.contains(event.target as Node)) {
        setProcedureOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const procedureOptions = useMemo(() => {
    const defaultOption: ServiceOption = {
      optionId: "legal-consultation",
      serviceId: "",
      canonicalServiceId: "",
      selectionLabel: copy.defaultProcedure,
      category: locale === "en" ? "General" : "General",
      aliases: [copy.defaultProcedure],
      searchTokens: ["consulta", "legal", "consultation"],
    };
    return [defaultOption, ...serviceOptions];
  }, [copy.defaultProcedure, locale, serviceOptions]);

  const selectedProcedure =
    procedureOptions.find((option) => option.optionId === procedureOptionId) ?? procedureOptions[0];

  const filteredProcedureOptions = useMemo(() => {
    const query = normalizeToken(procedureSearch);
    if (!query) return procedureOptions;
    return procedureOptions.filter((option) => {
      const candidates = [
        option.selectionLabel,
        option.category,
        option.serviceId,
        option.canonicalServiceId,
        ...(option.aliases ?? []),
        ...(option.searchTokens ?? []),
      ];
      return candidates.some((candidate) => normalizeToken(candidate).includes(query));
    });
  }, [procedureOptions, procedureSearch]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const cleanName = name.trim();
    const cleanPhone = phone.trim();
    const cleanLocalPhone = cleanPhone.replace(/[\s\-]/g, "");
    const combinedPhone = `${phonePrefix}${cleanLocalPhone}`;
    const cleanProcedure = selectedProcedure.selectionLabel.trim();

    if (!cleanName) {
      setError(copy.nameError);
      return;
    }
    if (!isValidPhone(combinedPhone)) {
      setError(copy.phoneError);
      return;
    }
    if (cleanProcedure.length < 3) {
      setError(copy.procedureError);
      return;
    }

    setSending(true);
    setError("");

    try {
      const pathname = window.location.pathname;
      const servicioSlug = toServicioSlug(selectedProcedure.canonicalServiceId || selectedProcedure.serviceId || "consulta-general");
      const { refId, origen } = createRefId(servicioSlug);
      const query = window.location.search.replace(/^\?/, "");
      const response = await fetch("/api/whatsapp-click", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source,
          locale,
          name: cleanName,
          phone: combinedPhone,
          procedure: cleanProcedure,
          procedureOptionId: selectedProcedure.optionId,
          serviceId: selectedProcedure.serviceId,
          canonicalServiceId: selectedProcedure.canonicalServiceId,
          inquiryCategory: selectedProcedure.category,
          pagePath: query ? `${pathname}?${query}` : pathname,
          refId,
          origen,
        }),
      });

      if (!response.ok) throw new Error("request_failed");
      const data = await response.json();
      if (typeof data?.url !== "string") throw new Error("missing_url");

      pushDataLayer(
        buildWhatsAppClickEvent({
          pagePath: pathname,
          servicio: servicioSlug,
          refId,
          origen,
          utmCampaign: getStoredAttribution().utm_campaign,
        }),
      );
      window.open(data.url, "_blank", "noopener,noreferrer");
      setOpen(false);
      setName("");
      setPhone("");
      setPhonePrefix("+34");
      setProcedureOptionId("legal-consultation");
      setProcedureSearch("");
    } catch {
      setError(copy.submitError);
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <button type="button" className={className} title={title} onClick={() => setOpen(true)}>
        {children}
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-xl border border-gray-700 bg-[#0d1424] p-6 shadow-2xl">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-white">{copy.title}</h2>
                <p className="mt-1 text-sm leading-5 text-gray-400">{copy.subtitle}</p>
              </div>
              <button
                type="button"
                className="rounded-lg px-2 py-1 text-xl leading-none text-gray-400 hover:text-white"
                onClick={() => setOpen(false)}
                aria-label={copy.cancel}
              >
                ×
              </button>
            </div>

            <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-300" htmlFor={`wa-name-${source}`}>
                  {copy.name}
                </label>
                <input
                  id={`wa-name-${source}`}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-white outline-none focus:border-[#25D366]"
                  placeholder={copy.namePlaceholder}
                  autoComplete="name"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-300" htmlFor={`wa-phone-${source}`}>
                  {copy.phone}
                </label>
                <div className="flex gap-2">
                  <PhonePrefixPicker value={phonePrefix} onChange={setPhonePrefix} variant="dark" />
                  <input
                    id={`wa-phone-${source}`}
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    className="min-w-0 flex-1 rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-white outline-none focus:border-[#25D366]"
                    placeholder={copy.phonePlaceholder}
                    autoComplete="tel"
                    inputMode="tel"
                  />
                </div>
              </div>

              <div ref={procedureRef} className="relative">
                <label className="mb-1 block text-sm font-medium text-gray-300" htmlFor={`wa-procedure-search-${source}`}>
                  {copy.procedure}
                </label>
                <button
                  type="button"
                  onClick={() => setProcedureOpen((value) => !value)}
                  className="flex w-full items-center justify-between gap-3 rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-left text-white outline-none transition-colors hover:border-gray-500 focus:border-[#25D366]"
                  aria-expanded={procedureOpen}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold">{selectedProcedure.selectionLabel}</span>
                    {selectedProcedure.category && (
                      <span className="block truncate text-xs text-gray-500">{selectedProcedure.category}</span>
                    )}
                  </span>
                  <span className={`shrink-0 text-gray-500 transition-transform ${procedureOpen ? "rotate-180" : ""}`}>
                    ▼
                  </span>
                </button>

                {procedureOpen && (
                  <div className="absolute left-0 right-0 top-full z-[110] mt-2 rounded-xl border border-gray-700 bg-gray-950 p-2 shadow-2xl">
                    <input
                      id={`wa-procedure-search-${source}`}
                      type="search"
                      value={procedureSearch}
                      onChange={(event) => setProcedureSearch(event.target.value)}
                      className="mb-2 w-full rounded-lg border border-gray-700 bg-[#0d1424] px-3 py-2 text-sm text-white outline-none focus:border-[#25D366]"
                      placeholder={copy.procedurePlaceholder}
                      autoComplete="off"
                    />
                    <div className="max-h-56 overflow-y-auto">
                      {servicesLoading && <p className="px-2 py-3 text-xs text-gray-500">{copy.servicesLoading}</p>}
                      {!servicesLoading && servicesLoadError && (
                        <p className="px-2 py-3 text-xs text-amber-300">{copy.servicesError}</p>
                      )}
                      {!servicesLoading && filteredProcedureOptions.length === 0 && (
                        <p className="px-2 py-3 text-xs text-gray-500">{copy.noServices}</p>
                      )}
                      {!servicesLoading &&
                        filteredProcedureOptions.map((option) => {
                          const isSelected = option.optionId === selectedProcedure.optionId;
                          return (
                            <button
                              key={option.optionId}
                              type="button"
                              onClick={() => {
                                setProcedureOptionId(option.optionId);
                                setProcedureSearch("");
                                setProcedureOpen(false);
                              }}
                              className={`mb-1 flex w-full items-start justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${
                                isSelected
                                  ? "border-[#25D366]/40 bg-[#25D366]/10 text-green-100"
                                  : "border-transparent text-gray-200 hover:border-gray-700 hover:bg-gray-900"
                              }`}
                            >
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-semibold">{option.selectionLabel}</span>
                                {option.category && (
                                  <span className="block truncate text-xs text-gray-500">{option.category}</span>
                                )}
                              </span>
                              {isSelected && <span className="shrink-0 text-sm text-[#25D366]">✓</span>}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>

              {error && <p className="text-sm text-red-300">{error}</p>}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="rounded-lg border border-gray-700 px-4 py-2 text-sm font-semibold text-gray-300 hover:border-gray-500"
                  onClick={() => setOpen(false)}
                >
                  {copy.cancel}
                </button>
                <button
                  type="submit"
                  disabled={sending}
                  className="rounded-lg bg-[#25D366] px-4 py-2 text-sm font-bold text-gray-950 hover:bg-green-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? copy.sending : copy.submit}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
